import { DatePicker, Form, Space, Typography } from 'antd'
import { FormProps, useForm, useWatch } from 'antd/lib/form/Form'
import { useProjectSettingsContext } from '../../../../../../ProjectSettingsContext'
import { useEffect, useState } from 'react'
import { toMoment } from 'utils/toMoment'
import CIMSFormItem from 'components/CIMSFormItem'
import { HelpMessage } from 'utils/constants'
import CIMSSwitch from 'components/CIMSSwitch'
import { IFIReportType, notEqual } from 'shared'
import CIMSTextArea from 'components/CIMSTextArea'
import { CommonSimpleEditPanelProps } from '../../../common'
import { useGlobalState } from 'context/GlobalStateContext'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { ifiSettingsEditNotification, momentNotEqual } from '../../../../utils'
import CIMSPrompt from 'components/CIMSPrompt'
import { useUpsertDensityTestIfiSettingsMutation } from 'schema'
import { DensityTestFormValues } from './types'
import EditPanelFooter from '../../../../components/EditPanelFooter'

const Help = HelpMessage.Administration.ProjectSettings.IFITracker
const {
  type: reportType,
  defaultMessages: { message },
} = IFIReportType.DensityTest

const DensityTestEditPanel: React.VFC<CommonSimpleEditPanelProps> = ({
  isDirty,
  setIsDirty,
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    densityTestIFISettings: { data, refetch },
  } = useProjectSettingsContext()
  const [upsert, { loading }] = useUpsertDensityTestIfiSettingsMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => ifiSettingsEditNotification(reportType),
  })
  const [initialValues, setInitialValues] = useState<DensityTestFormValues>({})
  const [form] = useForm<DensityTestFormValues>()
  const enabledWatch = useWatch('enabled', form)

  const handleOnValuesChange: FormProps<DensityTestFormValues>['onValuesChange'] =
    (_, currentValues) => {
      setIsDirty(
        notEqual(initialValues.enabled, currentValues.enabled) ||
          momentNotEqual(
            initialValues.effectiveDate,
            currentValues.effectiveDate
          ) ||
          notEqual(initialValues.message, currentValues.message)
      )
    }

  useEffect(() => {
    form.setFieldsValue(initialValues)
    handleOnValuesChange({}, form.getFieldsValue())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  useEffect(() => {
    setInitialValues({
      enabled: data?.enabled,
      effectiveDate: toMoment(data?.effectiveDate),
      message: data?.message,
    })
  }, [data])

  return (
    <CIMSPrompt when={isDirty}>
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={handleOnValuesChange}
        onFinish={async ({ enabled, effectiveDate, message }) => {
          if (!projectId) throw new Error('Project id must be defined')
          await upsert({
            variables: {
              input: {
                projectId,
                enabled,
                effectiveDate: effectiveDate?.toISOString(),
                message,
              },
            },
          })
          refetch()
        }}
      >
        <Typography>
          If the Average Percent is less than the Material Requirement's Lot
          Compaction Requirement, use the Message to issue an Identified Field
          Issue. Messages will always be followed by the actual and expected
          values.
        </Typography>
        <Space size="large" style={{ marginTop: '15px' }}>
          <CIMSFormItem
            label="Enabled"
            name="enabled"
            help={Help.Enabled}
            valuePropName="checked"
          >
            <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
          </CIMSFormItem>
          <CIMSFormItem
            label="Effective Date"
            name="effectiveDate"
            help={Help.EffectiveDate}
            disabled={{
              disabled: !enabledWatch,
              reason: {
                why: 'disabled',
                message: `Cannot edit while ${reportType} is disabled`,
              },
            }}
          >
            <DatePicker disabled={!enabledWatch} />
          </CIMSFormItem>
        </Space>
        <CIMSFormItem
          name="message"
          label="Message"
          help={Help.FailureMessage()}
          defaultMessage={message}
        >
          <CIMSTextArea />
        </CIMSFormItem>
        <EditPanelFooter
          {...{
            form,
            loading,
            handleOnValuesChange,
            hasChanges: isDirty,
          }}
        />
      </Form>
    </CIMSPrompt>
  )
}

export default DensityTestEditPanel
